import type { SearchSort } from '@/features/SearchWrapper/SearchWrapperTypes'
import type { staticSearchFacets, staticSearchFilters } from '@/types/galaxy-data/global'
import type { GalaxyUnitItem } from '@/types/galaxy-data/unit'
import { useDebounceFn } from '@vueuse/core'
import { ref, isRef, type Ref, unref, watch, computed, toRaw } from 'vue'

/**
 * Helper for useGalaxyStaticSearch
 *
 * Clean the facets object to get only the needed data
 * */
const formatAggregations = (aggregations: staticSearchFacets) => {
  const myObject: Record<string, Record<string | number, number>> = {}
  Object.keys(aggregations).forEach(function (key) {
    myObject[key] = {}
    aggregations[key].buckets.forEach(function (el) {
      myObject[key][el.key] = el.doc_count
    })
  })
  return myObject
}

interface ConfigParameters<Type> {
  /** Array of your Galaxy Unit Data */
  rawData: Type[] | Ref<Type[]>
  /** Static Search Filters (key value object) */
  filters?: staticSearchFilters | Ref<staticSearchFilters>
  currentSort?: Ref<SearchSort | undefined>
}

interface SearchItem extends GalaxyUnitItem {
  displayPrice?: {
    currencyCode: string
    value: number
  }
}

/**
 * Use Galaxy Static Search on unit JSON items
 *
 * This will watch any change on your data source / your filters
 * and filter data accordingly
 */
export function useGalaxyStaticSearch<Type extends SearchItem>(config: ConfigParameters<Type>) {
  const filteredResults = ref<Type[]>([]) as Ref<Type[]>
  const isLoading = ref(true)
  const count = ref(0)
  const rawAggregations = ref({}) as Ref<staticSearchFacets>

  const updateData = () => {
    isLoading.value = true
    filterData()
  }

  /**
   * Send the data to GalaxyStatic API and filter it
   * Debounced just in case for performances
   */
  const filterData = useDebounceFn(
    async () => {
      const data = unref(config.rawData)

      const filters = {}
      const custom_filters = []
      if (config.filters) {
        for (const key in unref(config.filters)) {
          if (['total_area', 'meeting_room_max_occupancy'].indexOf(key) >= 0)
            filters[key] = toRaw(config.filters.value[key]).join(' || ')
          else if (['event_room_size'].indexOf(key) >= 0) {
            custom_filters.push({ key: key, value: toRaw(config.filters.value[key]).join(' || ') })
            delete filters[key]
          } else filters[key] = toRaw(config.filters.value[key])
        }
      }

      //create virtual fields (should be refactor and filter without this fix)
      data.forEach((item) => {
        if (item?.functionRoomSetups) {
          item.functionRoomSetupsValues = item.functionRoomSetups.map((f) => f?.cle)
        }
        if (item?.meeting_room_amenities) {
          item.amenities_value = item.meeting_room_amenities.map((f) => f?.id.toString())
        }
        if (item?.hotel_meeting_room) {
          item.event_room_size = item.hotel_meeting_room.map((f) =>
            f ? parseInt(f.total_area) : undefined,
          )
        }
        if (item?.total_area) {
          item.total_area = parseInt(item.total_area)
        }
      })

      // ensure filters is a plain raw object and not a reactive or ref
      const response = await window.GalaxyHelpers.staticSearch<Type>({
        data,
        filters,
      }) // TODO: xo7: handling errors
      if (custom_filters.length)
        custom_filters.forEach((filter) => {
          if (filter.value != '') {
            let hasValueRange, operator, numRange, minRange
            //console.log(response.results)
            response.results.forEach((item, idx) => {
              hasValueRange = false
              minRange = -1
              item[filter.key]?.forEach((val) => {
                operator = filter.value.replace(/\d+/g, '')
                numRange = filter.value.replace(/\D/g, '')
                if (operator == '<=') {
                  //console.log(minRange + " " + operator + " " + val + " " + operator + " " + numRange)
                  if (val && minRange <= val && val <= numRange) hasValueRange = true
                }
                if (operator == '>') {
                  //console.log(val + " " + operator + " " + numRange)
                  if (val && val > numRange) hasValueRange = true
                }
                minRange = numRange
              })
              //console.log(item)
              //console.log(hasValueRange)
              if (!hasValueRange) {
                response.results.splice(idx, 1)
                response.count--
              }
            })
          }
        })
      //console.log(response.results)
      count.value = response.count
      filteredResults.value = response.results
      rawAggregations.value = response.aggregations || {}
      isLoading.value = false
    },
    150,
    { maxWait: 1000 },
  )

  const mapNbItemsPerFilteringOption = computed(() => {
    if (rawAggregations.value) {
      return formatAggregations(rawAggregations.value)
    }
    return {}
  })

  watch(config.rawData, () => {
    updateData()
  })
  if (isRef(config.filters)) {
    watch(
      config.filters,
      () => {
        updateData()
      },
      { deep: true },
    )
  }

  updateData()

  const sortedResults = computed(() => {
    switch (config.currentSort?.value) {
      case 'price-desc':
        return [...filteredResults.value].sort((a, b) => {
          if (a.displayPrice?.value && b.displayPrice?.value) {
            return b.displayPrice?.value - a.displayPrice?.value
          } else if (a.displayPrice?.value) {
            return -1
          } else if (b.displayPrice?.value) {
            return 1
          } else {
            return 0
          }
        })
      case 'price-asc':
        return [...filteredResults.value].sort((a, b) => {
          if (a.displayPrice?.value && b.displayPrice?.value) {
            return a.displayPrice?.value - b.displayPrice?.value
          } else if (a.displayPrice?.value) {
            return -1
          } else if (b.displayPrice?.value) {
            return 1
          } else {
            return 0
          }
        })
      default:
        return filteredResults.value
    }
  })

  return { sortedResults, isLoading, count, mapNbItemsPerFilteringOption }
}
